import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, Input, Spinner } from "@nutrayan/ui";
import { formatCurrency } from "@nutrayan/utils";
import { apiClient } from "@/lib/api-client";

// Contract: there is no admin product-list endpoint yet (BLK-API-002) —
// the public published-product list is shown, with client-side name search.
// DRAFT products won't appear until the backend exposes an admin list.
interface ProductSummary {
  id: number;
  slug?: string;
  name?: string;
  line?: string;
  status?: string;
  madeForIndia?: boolean;
  fromPricePaise?: number;
}

export default function CatalogListPage() {
  const [q, setQ] = useState("");
  const query = useQuery({
    queryKey: ["admin", "products"],
    queryFn: () => apiClient.request<ProductSummary[]>("/api/v1/catalog/products?page=0&size=50"),
  });

  const all = query.data ?? [];
  const rows = q ? all.filter((p) => (p.name ?? "").toLowerCase().includes(q.toLowerCase())) : all;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-display-xl text-charcoal">Catalog</h1>
          <p className="text-body text-ink">
            Published products. (Draft listing pending an admin list endpoint — BLK-API-002.)
          </p>
        </div>
        <Link to="/catalog/new">
          <Button size="md">+ New product</Button>
        </Link>
      </header>

      <div className="flex items-center gap-2">
        <Input
          placeholder="Search products"
          aria-label="Search products"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {query.isPending ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : query.isError ? (
        <Card>
          <p className="text-body text-terracotta">
            Couldn&apos;t load the catalog. Check API connectivity.
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <p className="text-body text-ink">No products match.</p>
        </Card>
      ) : (
        <Card className="p-0">
          <table className="text-body-sm w-full">
            <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Line</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">From price</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-charcoal/5 border-b">
                  <td className="text-charcoal px-4 py-2">{p.name ?? "—"}</td>
                  <td className="text-ink px-4 py-2 font-mono">{p.slug ?? "—"}</td>
                  <td className="text-ink px-4 py-2">{(p.line ?? "—").toLowerCase()}</td>
                  <td className="text-ink px-4 py-2">{(p.status ?? "—").toLowerCase()}</td>
                  <td className="price text-charcoal px-4 py-2 text-right">
                    {typeof p.fromPricePaise === "number" ? formatCurrency(p.fromPricePaise) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
